import { UserIcon } from '@heroicons/react/24/outline'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useLogout } from '@/components/layouts/Header/User/useLogout'
import { UserMenuContent } from '@/components/layouts/Header/User/UserMenuContent'
import { useUserHasTodos } from '@/components/layouts/Header/User/useUserHasTodos'
import { useSignInUrl } from '@/components/shared/hooks/useSignInUrl'
import { Img } from '@/components/shared/Img'
import { playwrightTestId } from '@/components/shared/utils/playwright'
import { currentUserQueryOptions } from '@/server/users/currentUserQueryOptions'
import type { CurrentUser } from '@/server/users/queries/getCurrentUser.server'
import { ControlButtonDot } from '../ControlButtonDot'
import { MobileBottomSheet } from './MobileBottomSheet'
import {
  mobileControlButtonActiveClassName,
  mobileMapIconButtonClassName,
} from './mobileControlButton.const'

const MobileUserLoggedIn = ({ user }: { user: NonNullable<CurrentUser> }) => {
  const [open, setOpen] = useState(false)
  const handleLogout = useLogout()
  const hasTodos = useUserHasTodos(user)

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Benutzerkonto"
        aria-expanded={open}
        data-testid={playwrightTestId('user-info')}
        className={twMerge(
          mobileMapIconButtonClassName,
          'relative',
          open && mobileControlButtonActiveClassName,
        )}
      >
        {user.osmAvatar ? (
          <Img
            src={user.osmAvatar}
            width={28}
            height={28}
            className="size-7 rounded-full"
            alt=""
            aria-hidden
          />
        ) : (
          <UserIcon className="size-6 text-gray-600" aria-hidden="true" />
        )}
        {hasTodos && (
          <ControlButtonDot srLabel="Es fehlen wichtige Informationen für den Account." />
        )}
      </button>

      <MobileBottomSheet
        open={open}
        onClose={() => setOpen(false)}
        title={`Angemeldet als ${user.osmName}`}
      >
        <UserMenuContent user={user} />
        <div className="p-1">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full rounded-md px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
          >
            Ausloggen
          </button>
        </div>
      </MobileBottomSheet>
    </>
  )
}

const MobileUserLoggedOut = () => {
  const signInHref = useSignInUrl()

  return (
    <a href={signInHref} aria-label="Anmelden" className={mobileMapIconButtonClassName}>
      <UserIcon className="size-6" aria-hidden="true" />
    </a>
  )
}

/**
 * Mobile user control (placed top-right in the MobileMapHeader): an avatar
 * button that opens a bottom sheet with the account info + logout when signed
 * in, or an "Anmelden" button when signed out.
 *
 * The contact-profile prompt and cookie cleanup are NOT rendered here: the
 * desktop header (hidden on mobile via `hidden sm:block`, but still mounted)
 * already owns the `<User>` instances that render them, so adding them here
 * would duplicate the portaled modal.
 */
export const MobileUserMenu = () => {
  const { data } = useQuery(currentUserQueryOptions())
  const user = data?.user ?? null

  return user ? <MobileUserLoggedIn user={user} /> : <MobileUserLoggedOut />
}
